import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { when } from "@/lib/rides";
import { LocationPicker } from "@/components/LocationPicker";
import { toLocalInput } from "@/components/booking/TripOptions";
import { CLOSED_MSG, canModify, cutoffAt } from "@/lib/rider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Scope({ value, onChange }) {
  return (
    <div className="space-y-2 rounded-xl border border-white/10 p-3 text-sm" data-testid="modify-scope">
      {[["this", "Only this ride"], ["series", "This and all later rides in this recurring schedule"]].map(([v, label]) => (
        <label key={v} className="flex cursor-pointer items-center gap-2">
          <input type="radio" name="scope" checked={value === v} onChange={() => onChange(v)} data-testid={`modify-scope-${v}`} className="accent-[hsl(var(--primary))]" />
          {label}
        </label>
      ))}
    </div>
  );
}

function Form({ ride, onDone, defaultScope }) {
  const qc = useQueryClient();
  const [pickup, setPickup] = useState(ride.pickup);
  const [dropoff, setDropoff] = useState(ride.dropoff);
  const [at, setAt] = useState(toLocalInput(ride.scheduled_at));
  const [passengers, setPassengers] = useState(ride.passengers || 1);
  const [scope, setScope] = useState(defaultScope);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const changedTime = new Date(at).getTime() !== new Date(ride.scheduled_at).getTime();
      const { data } = await api.patch(`/rides/${ride.id}`, {
        pickup, dropoff, passengers, apply_to: scope, scheduled_at: changedTime ? new Date(at).toISOString() : null,
      });
      toast.success(`${data.updated.length} ride${data.updated.length > 1 ? "s" : ""} updated`, {
        description: data.skipped.length ? `${data.skipped.length} could not be changed: ${data.skipped[0].reason}` : "Your driver and Scoopz were notified.",
      });
      ["myRides", "activeRide", "mySeries", "credit"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onDone();
    } catch (e) {
      toast.error(formatApiError(e), { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground" data-testid="modify-policy">Changes are allowed until 6 hours before pickup (until {when(cutoffAt(ride).toISOString())}). Your fare is recalculated with the standard Scoopz pricing.</p>
      <LocationPicker kind="modify-pickup" label="Pickup" value={pickup} onChange={setPickup} allowGps />
      <LocationPicker kind="modify-dropoff" label="Destination" value={dropoff} onChange={setDropoff} allowGps />
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="h-10 rounded-full [color-scheme:dark]" data-testid="modify-datetime-input" />
        <select value={passengers} onChange={(e) => setPassengers(Number(e.target.value))} data-testid="modify-passengers-select"
          className="h-10 rounded-full border bg-background px-3 text-sm">{[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n} pax</option>)}</select>
      </div>
      {ride.series_id && <Scope value={scope} onChange={setScope} />}
      <Button className="h-11 w-full rounded-full font-semibold" onClick={save} disabled={busy || !pickup || !dropoff || !at} data-testid="modify-save-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
      </Button>
    </div>
  );
}

export function ModifyDialog({ ride, open, onOpenChange, defaultScope = "this" }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-md" data-testid="modify-dialog">
        <DialogHeader><DialogTitle className="font-display">Modify ride</DialogTitle></DialogHeader>
        {canModify(ride) ? <Form ride={ride} onDone={() => onOpenChange(false)} defaultScope={defaultScope} />
          : <p className="rounded-xl bg-amber-400/10 p-3 text-sm text-amber-200" data-testid="modify-closed">{CLOSED_MSG}</p>}
      </DialogContent>
    </Dialog>
  );
}
